import React, { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { importService, ImportResponse } from '../services/importService';
import { integrationService, WFirmaImportResult, WFirmaMatchOnlyResult } from '../services/integrationService';
import { useTranslation } from '../hooks/useTranslation';

interface ImportCounterpartiesModalProps {
  companyId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ImportSource = 'file' | 'wfirma' | null;

const ImportCounterpartiesModal: React.FC<ImportCounterpartiesModalProps> = ({
  companyId,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [importSource, setImportSource] = useState<ImportSource>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importFormat, setImportFormat] = useState<'csv' | 'excel'>('csv');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Проверяем наличие конфигурации wFirma
  const { data: hasWFirmaConfig = false } = useQuery({
    queryKey: ['wfirma-config', companyId],
    queryFn: () => integrationService.hasWFirmaConfig(companyId),
    enabled: isOpen,
    retry: false
  });

  const downloadTemplateMutation = useMutation({
    mutationFn: (format: 'csv' | 'excel') => importService.downloadCounterpartiesTemplate(companyId, format),
    onSuccess: (blob, format) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `counterparties_template.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => importService.importCounterparties(companyId, file),
    onSuccess: (data: ImportResponse) => {
      if (data.errorCount === 0) {
        onSuccess();
        onClose();
        setSelectedFile(null);
        setImportSource(null);
      }
    }
  });

  const wfirmaImportMutation = useMutation({
    mutationFn: () => integrationService.importContractorsFromWFirma(companyId),
    onSuccess: (data: WFirmaImportResult) => {
      onSuccess();
      // Не закрываем модальное окно сразу, чтобы показать результаты
    }
  });

  const wfirmaMatchOnlyMutation = useMutation({
    mutationFn: () => integrationService.matchOnlyContractorsFromWFirma(companyId),
    onSuccess: () => {
      onSuccess();
    }
  });

  // Сбрасываем состояние при закрытии
  useEffect(() => {
    if (!isOpen) {
      setImportSource(null);
      setSelectedFile(null);
      importMutation.reset();
      wfirmaImportMutation.reset();
      wfirmaMatchOnlyMutation.reset();
    }
  }, [isOpen]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Определяем формат файла
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (extension === 'xlsx' || extension === 'xls') {
        setImportFormat('excel');
      } else {
        setImportFormat('csv');
      }
    }
  };

  const handleImport = () => {
    if (selectedFile) {
      importMutation.mutate(selectedFile);
    }
  };

  const handleWFirmaImport = () => {
    wfirmaImportMutation.mutate();
  };

  const handleWFirmaMatchOnly = () => {
    wfirmaMatchOnlyMutation.mutate();
  };

  const handleDownloadTemplate = (format: 'csv' | 'excel') => {
    downloadTemplateMutation.mutate(format);
  };

  const handleBack = () => {
    setImportSource(null);
    setSelectedFile(null);
    importMutation.reset();
    wfirmaImportMutation.reset();
    wfirmaMatchOnlyMutation.reset();
  };

  if (!isOpen) return null;

  // Первый этап: выбор источника импорта
  if (importSource === null) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('companies.import.counterparties.title') || 'Импорт контрагентов'}
            </h3>
            
            <p className="text-sm text-gray-600 mb-6">
              {t('companies.import.selectSource') || 'Выберите источник импорта'}
            </p>

            <div className="space-y-3">
              <button
                onClick={() => setImportSource('file')}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-left"
              >
                <div className="font-medium">{t('companies.import.fromFile') || 'Импорт из файла'}</div>
                <div className="text-sm opacity-90">{t('companies.import.fromFileDescription') || 'CSV или Excel файл'}</div>
              </button>

              <button
                onClick={() => setImportSource('wfirma')}
                disabled={!hasWFirmaConfig}
                className={`w-full px-4 py-3 rounded-lg transition-colors text-left ${
                  hasWFirmaConfig
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <div className="font-medium">{t('companies.import.fromWFirma') || 'Импорт из wFirma'}</div>
                {hasWFirmaConfig ? (
                  <div className="text-sm opacity-90">{t('companies.import.fromWFirmaDescription') || 'Импорт всех контрагентов из wFirma'}</div>
                ) : (
                  <div className="text-sm">{t('companies.import.wfirmaNotConfigured') || 'Сначала настройте интеграцию wFirma в настройках компании'}</div>
                )}
              </button>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                {t('common.close') || 'Закрыть'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Импорт из файла
  if (importSource === 'file') {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('companies.import.counterparties.title') || 'Импорт контрагентов'}
            </h3>

          {/* Кнопки скачивания шаблона */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('companies.import.downloadTemplate') || 'Скачать шаблон'}
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => handleDownloadTemplate('csv')}
                disabled={downloadTemplateMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                CSV
              </button>
              <button
                onClick={() => handleDownloadTemplate('excel')}
                disabled={downloadTemplateMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                Excel
              </button>
            </div>
          </div>

          {/* Выбор файла */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('companies.import.selectFile') || 'Выбрать файл'}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {selectedFile && (
              <p className="mt-2 text-sm text-gray-600">
                {t('companies.import.selectedFile') || 'Выбран файл'}: {selectedFile.name}
              </p>
            )}
          </div>

          {/* Результаты импорта */}
          {importMutation.data && (
            <div className={`mb-4 p-3 rounded-md ${
              importMutation.data.errorCount === 0 
                ? 'bg-green-50 text-green-800' 
                : 'bg-yellow-50 text-yellow-800'
            }`}>
              <p className="font-medium">
                {t('companies.import.imported') || 'Импортировано'}: {importMutation.data.importedCount}
              </p>
              {importMutation.data.errorCount > 0 && (
                <div className="mt-2">
                  <p className="font-medium">
                    {t('companies.import.errors') || 'Ошибки'}: {importMutation.data.errorCount}
                  </p>
                  <ul className="list-disc list-inside mt-1 text-sm">
                    {importMutation.data.errors.slice(0, 5).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Кнопки */}
          <div className="flex justify-end gap-2">
            <button
              onClick={handleBack}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              {t('common.back') || 'Назад'}
            </button>
            <button
              onClick={() => {
                onClose();
                setSelectedFile(null);
                importMutation.reset();
                setImportSource(null);
              }}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              {t('common.close') || 'Закрыть'}
            </button>
            <button
              onClick={handleImport}
              disabled={!selectedFile || importMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {importMutation.isPending 
                ? (t('companies.import.importing') || 'Импорт...') 
                : (t('companies.import.import') || 'Импортировать')}
            </button>
          </div>
        </div>
      </div>
    </div>
    );
  }

  // Импорт из wFirma
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t('companies.import.fromWFirma') || 'Импорт из wFirma'}
          </h3>

          <p className="text-sm text-gray-600 mb-4">
            {t('companies.import.wfirmaImportDescription') || 'Импортирует все контрагенты из wFirma. Существующие контрагенты будут обновлены.'}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {t('companies.import.wfirmaMatchOnlyHint') ||
              'Только сопоставить: проставить wFirma ID существующим контрагентам без создания новых. Неоднозначные случаи пропускаются.'}
          </p>

          {/* Результаты импорта */}
          {wfirmaImportMutation.data && (
            <div className={`mb-4 p-3 rounded-md ${
              wfirmaImportMutation.data.errors === 0 
                ? 'bg-green-50 text-green-800' 
                : 'bg-yellow-50 text-yellow-800'
            }`}>
              <p className="font-medium">
                {t('companies.import.created') || 'Создано'}: {wfirmaImportMutation.data.created}
              </p>
              <p className="font-medium">
                {t('companies.import.updated') || 'Обновлено'}: {wfirmaImportMutation.data.updated}
              </p>
              {wfirmaImportMutation.data.skipped > 0 && (
                <p className="font-medium">
                  {t('companies.import.skipped') || 'Пропущено'}: {wfirmaImportMutation.data.skipped}
                </p>
              )}
              {wfirmaImportMutation.data.errors > 0 && (
                <div className="mt-2">
                  <p className="font-medium">
                    {t('companies.import.errors') || 'Ошибки'}: {wfirmaImportMutation.data.errors}
                  </p>
                  <ul className="list-disc list-inside mt-1 text-sm">
                    {wfirmaImportMutation.data.errorMessages.slice(0, 5).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Результат только сопоставления */}
          {wfirmaMatchOnlyMutation.data && (
            <div
              className={`mb-4 p-3 rounded-md ${
                (wfirmaMatchOnlyMutation.data as WFirmaMatchOnlyResult).errors === 0
                  ? 'bg-green-50 text-green-800'
                  : 'bg-yellow-50 text-yellow-800'
              }`}
            >
              <p className="font-medium">
                {t('companies.import.matchOnlyLinked') || 'Привязано'}:{' '}
                {(wfirmaMatchOnlyMutation.data as WFirmaMatchOnlyResult).linked}
              </p>
              <p className="font-medium">
                {t('companies.import.matchOnlySkippedAmbiguous') || 'Пропущено (несколько в wFirma)'}:{' '}
                {(wfirmaMatchOnlyMutation.data as WFirmaMatchOnlyResult).skippedAmbiguous}
              </p>
              <p className="font-medium">
                {t('companies.import.matchOnlySkippedNoMatch') || 'Без совпадения'}:{' '}
                {(wfirmaMatchOnlyMutation.data as WFirmaMatchOnlyResult).skippedNoMatch}
              </p>
              {(wfirmaMatchOnlyMutation.data as WFirmaMatchOnlyResult).errors > 0 && (
                <div className="mt-2">
                  <p className="font-medium">
                    {t('companies.import.errors') || 'Ошибки'}:{' '}
                    {(wfirmaMatchOnlyMutation.data as WFirmaMatchOnlyResult).errors}
                  </p>
                  <ul className="list-disc list-inside mt-1 text-sm">
                    {(wfirmaMatchOnlyMutation.data as WFirmaMatchOnlyResult).errorMessages
                      .slice(0, 5)
                      .map((msg, index) => (
                        <li key={index}>{msg}</li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Кнопки */}
          <div className="flex flex-wrap justify-end gap-2">
            <button
              onClick={handleBack}
              disabled={wfirmaImportMutation.isPending || wfirmaMatchOnlyMutation.isPending}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
            >
              {t('common.back') || 'Назад'}
            </button>
            <button
              onClick={() => {
                onClose();
                setImportSource(null);
                wfirmaImportMutation.reset();
                wfirmaMatchOnlyMutation.reset();
              }}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              {t('common.close') || 'Закрыть'}
            </button>
            <button
              onClick={handleWFirmaMatchOnly}
              disabled={wfirmaMatchOnlyMutation.isPending || wfirmaImportMutation.isPending}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {wfirmaMatchOnlyMutation.isPending
                ? t('companies.import.matchOnlyRunning') || 'Сопоставление...'
                : t('companies.import.matchOnlyFromWfirma') || 'Только сопоставить'}
            </button>
            <button
              onClick={handleWFirmaImport}
              disabled={wfirmaImportMutation.isPending || wfirmaMatchOnlyMutation.isPending}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {wfirmaImportMutation.isPending 
                ? (t('companies.import.importing') || 'Импорт...') 
                : (t('companies.import.import') || 'Импортировать')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportCounterpartiesModal;

